// src/pages/WebGisPage.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios'; // Pastikan axios terimport di sini
import { useNavigate } from 'react-router-dom';
import L from 'leaflet'; // Import Leaflet library
import 'leaflet/dist/leaflet.css'; // Import Leaflet CSS
import { FaSearch, FaPlus, FaDownload } from 'react-icons/fa'; // Semua import di sini

import '../styles/WebGisPage.css'; // Ini juga harus jadi import terakhir jika Anda punya CSS

// FIX: Leaflet default icon issues - Pastikan baris ini di sini, setelah SEMUA import
delete L.Icon.Default.prototype._get; // Perhatikan semicolon
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});


const WebGisPage = () => {
    const navigate = useNavigate();
    const mapRef = useRef(null); // Ref untuk elemen peta
    const leafletMapRef = useRef(null); // Ref untuk objek peta Leaflet

    const [jwtToken, setJwtToken] = useState(null); //
    const [allDepotData, setAllDepotData] = useState([]); //
    const [filteredDepotData, setFilteredDepotData] = useState([]); // Data yang sudah difilter/cari

    const [searchKeyword, setSearchKeyword] = useState(''); //
    const [filterStatus, setFilterStatus] = useState(''); //
    const [addMode, setAddMode] = useState(false); // Untuk toggle form tambah titik
    const [newPointFormData, setNewPointFormData] = useState({ // State untuk form tambah titik
        nama: '', alamat: '', telepon: '', jam_buka: '',
        latitude: '', longitude: '', kelurahan: '', kecamatan: '',
        kota_kabupaten: '', surveyed_by: '', is_subscribed: 'Under Prospect'
    });

    const [summary, setSummary] = useState({ total: 0, ya: 0, tidak: 0, prospect: 0 }); //
    const [loading, setLoading] = useState(true); //
    const [error, setError] = useState(''); //
    const [message, setMessage] = useState(''); //

    // --- Autentikasi dan Redirect ---
    useEffect(() => {
        const token = localStorage.getItem('token');
        const isSuperAdmin = localStorage.getItem('is_super_admin') === 'true';

        if (!token || !isSuperAdmin) {
            alert('Akses ditolak. Anda bukan Super Admin atau belum login.');
            navigate('/login', { replace: true });
            return;
        }
        setJwtToken(token);
    }, [navigate]); //

    // --- Inisialisasi Peta Leaflet ---
    useEffect(() => {
        if (!jwtToken || leafletMapRef.current) return; // Jangan inisialisasi ulang jika sudah ada atau belum ada token

        leafletMapRef.current = L.map(mapRef.current).setView([-6.2, 106.8], 11);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(leafletMapRef.current);

        // Event listener untuk klik peta saat addMode aktif
        leafletMapRef.current.on('click', (e) => {
            if (addMode) {
                setNewPointFormData(prev => ({
                    ...prev,
                    latitude: e.latlng.lat.toFixed(6),
                    longitude: e.latlng.lng.toFixed(6)
                }));
            }
        });

        // Hapus peta saat komponen di-unmount
        return () => {
            leafletMapRef.current.remove();
            leafletMapRef.current = null;
        };
    }, [jwtToken, addMode]); // Dependency jwtToken untuk memastikan token sudah ada, addMode untuk update event listener

    // --- Fetch Data Depot ---
    const fetchDepotData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get('https://depotaircerdas.com/api/webgis/depot', {
                headers: { 'Authorization': `Bearer ${jwtToken}` }
            });
            setAllDepotData(response.data);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching depot data:', err);
            setError('Gagal mengambil data depot. ' + (err.response?.data?.message || err.message));
            setLoading(false);
            setAllDepotData([]); // Pastikan tetap array kosong
        }
    }, [jwtToken]); //

    useEffect(() => {
        if (jwtToken) {
            fetchDepotData();
        }
    }, [jwtToken, fetchDepotData]); //

    // --- Apply Filters and Render Markers ---
    const applyFiltersAndRenderMarkers = useCallback(() => {
        if (!leafletMapRef.current || !allDepotData.length) return;

        // Hapus semua marker yang ada
        leafletMapRef.current.eachLayer(l => {
            if (l instanceof L.CircleMarker) {
                leafletMapRef.current.removeLayer(l);
            }
        });

        let currentFilteredData = allDepotData.filter(d => {
            const keywordMatch = !searchKeyword ||
                d.nama.toLowerCase().includes(searchKeyword.toLowerCase()) ||
                d.alamat.toLowerCase().includes(searchKeyword.toLowerCase());
            const statusMatch = !filterStatus || d.is_subscribed === filterStatus;
            return keywordMatch && statusMatch;
        });

        setFilteredDepotData(currentFilteredData);

        let countTotal = 0, countYa = 0, countTidak = 0, countProspect = 0;

        currentFilteredData.forEach(d => {
            let color = 'orange'; // Under Prospect
            if (d.is_subscribed === 'Ya') {
                color = 'green';
                countYa++;
            } else if (d.is_subscribed === 'Tidak') {
                color = 'red';
                countTidak++;
            } else {
                countProspect++;
            }
            countTotal++;

            const popupContent = `
                <div style='font-size:14px; max-width:250px;'>
                    <b style='font-size:16px;'>${d.nama?.toUpperCase()}</b><br>
                    <i>${d.alamat}</i><br><br>
                    <b>📞 Telepon:</b> ${d.telepon || '-'}<br>
                    <b>⏰ Jam Buka:</b> ${d.jam_buka || '-'}<br>
                    <b>📍 Lokasi:</b> ${parseFloat(d.latitude).toFixed(5)}, ${parseFloat(d.longitude).toFixed(5)}<br>
                    <b>🗺 Kelurahan:</b> ${d.kelurahan || '-'}<br>
                    <b>🏘 Kecamatan:</b> ${d.kecamatan || '-'}<br>
                    <b>🏙 Kota/Kab:</b> ${d.kota_kabupaten || '-'}<br>
                    <b>⭐ Rating:</b> ${d.rating ?? '0'}<br>
                    <b>📝 Ulasan:</b> ${d.jumlah_ulasan ?? '0'}<br><br>
                    <b>Surveyor:</b><br><input type="text" id="surveyor_${d.id}" value="${d.surveyed_by || ''}" style="width:95%"><br>
                    <b>Status Langganan:</b>
                    <select id="subscribed_${d.id}" style="width:95%">
                        <option value="Ya" ${d.is_subscribed === "Ya" ? "selected" : ""}>Ya</option>
                        <option value="Tidak" ${d.is_subscribed === "Tidak" ? "selected" : ""}>Tidak</option>
                        <option value="Under Prospect" ${d.is_subscribed === "Under Prospect" ? "selected" : ""}>Under Prospect</option>
                    </select><br>
                    <button onclick="window.updateDepotDataFromPopup(${d.id})">Simpan</button>
                </div>
            `;

            const marker = L.circleMarker([parseFloat(d.latitude), parseFloat(d.longitude)], {
                radius: 8, fillColor: color, color: "#000", weight: 1, opacity: 1, fillOpacity: 0.8
            });
            marker.bindPopup(popupContent);
            marker.addTo(leafletMapRef.current);
        });

        setSummary({ total: countTotal, ya: countYa, tidak: countTidak, prospect: countProspect });
    }, [allDepotData, searchKeyword, filterStatus]);

    // Panggil fungsi filter saat data berubah atau filter berubah
    useEffect(() => {
        applyFiltersAndRenderMarkers();
    }, [allDepotData, searchKeyword, filterStatus, applyFiltersAndRenderMarkers]);


    // --- Tambah Titik Baru ---
    const handleAddFormChange = (e) => {
        const { name, value } = e.target;
        setNewPointFormData(prev => ({ ...prev, [name]: value }));
    };

    const toggleAddForm = () => {
        setAddMode(prev => !prev);
        // Reset form data saat form ditutup
        if (addMode) {
            setNewPointFormData({
                nama: '', alamat: '', telepon: '', jam_buka: '',
                latitude: '', longitude: '', kelurahan: '', kecamatan: '',
                kota_kabupaten: '', surveyed_by: '', is_subscribed: 'Under Prospect'
            });
        }
    };

    const submitNewPoint = async () => {
        setLoading(true);
        setError('');
        setMessage('');
        try {
            const data = { ...newPointFormData };
            data.latitude = parseFloat(data.latitude);
            data.longitude = parseFloat(data.longitude);

            // ✅ Ganti 'response' dengan '_' untuk menghindari warning no-unused-vars
            const _ = await axios.post('https://depotaircerdas.com/api/webgis/depot', data, {
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${jwtToken}` }
            });
            setMessage('✅ Titik berhasil ditambahkan.');
            toggleAddForm(); // Tutup form
            fetchDepotData(); // Refresh data peta
        } catch (err) {
            console.error('Error adding new point:', err);
            setError('❌ Gagal menambahkan titik. ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    // --- Update Data dari Pop-up (Penting: Mekanisme global untuk Vanilla JS di Popup) ---
    // Karena pop-up Leaflet merender HTML string, kita perlu membuat fungsi global
    // yang bisa diakses oleh onclick event di dalam HTML string tersebut.
    // Ini bukan praktik React murni, tapi perlu untuk integrasi dengan Leaflet pop-up.
    // ✅ Wrap dengan useCallback untuk mengatasi warning react-hooks/exhaustive-deps
    const updateDepotDataFromPopup = useCallback(async (id) => {
        // Ambil nilai dari DOM secara langsung karena di luar siklus React
        const surveyed_by_el = document.getElementById(`surveyor_${id}`);
        const is_subscribed_el = document.getElementById(`subscribed_${id}`);

        if (!surveyed_by_el || !is_subscribed_el) {
            alert("Elemen input tidak ditemukan di popup!");
            return;
        }

        const surveyed_by = surveyed_by_el.value;
        const is_subscribed = is_subscribed_el.value;

        setLoading(true); // Ini akan memengaruhi komponen utama
        setError('');
        setMessage('');

        try {
            // ✅ Ganti 'response' dengan '_' untuk menghindari warning no-unused-vars
            const _ = await axios.put(`https://depotaircerdas.com/api/webgis/depot/${id}`, {
                surveyed_by,
                is_subscribed
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${jwtToken}`
                }
            });
            alert("✅ Data berhasil diperbarui."); // Gunakan alert karena di luar render React langsung
            setMessage("Data berhasil diperbarui di peta.");
            fetchDepotData(); // Refresh semua data untuk menampilkan perubahan
        } catch (err) {
            console.error("Error updating data from popup:", err);
            alert("❌ Gagal menyimpan data: " + (err.response?.data?.message || err.message));
            setError("Gagal menyimpan data dari popup.");
        } finally {
            setLoading(false);
        }
    }, [jwtToken, fetchDepotData, setLoading, setError, setMessage]); // ✅ Dependencies untuk useCallback

    // Pasang fungsi global ini ke window agar bisa diakses dari HTML string di pop-up
    useEffect(() => {
        window.updateDepotDataFromPopup = updateDepotDataFromPopup;
        return () => {
            delete window.updateDepotDataFromPopup; // Bersihkan saat unmount
        };
    }, [updateDepotDataFromPopup]);


    // --- Ekspor CSV ---
    const exportCSV = () => {
        const rows = [["Nama", "Alamat", "Telepon", "Jam Buka", "Latitude", "Longitude", "Kelurahan", "Kecamatan", "Kota", "Rating", "Ulasan", "Surveyor", "Status Langganan"]];
        filteredDepotData.forEach(d => {
            rows.push([
                d.nama, d.alamat, d.telepon, d.jam_buka, d.latitude, d.longitude,
                d.kelurahan, d.kecamatan, d.kota_kabupaten, d.rating ?? 0, d.jumlah_ulasan ?? 0,
                d.surveyed_by || '', d.is_subscribed || ''
            ]);
        });
        const csv = rows.map(e => e.map(v => `"${v}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "depot_data.csv";
        link.click();
    };

    if (!jwtToken) return null; // Jangan render sampai token diverifikasi

    return (
        <div className="webgis-container">
            <h2 className="webgis-title">WebGIS Depot Air Cerdas</h2>

            {/* Controls Section */}
            <div className="controls-panel">
                <div className="search-filter-group">
                    <div className="search-input-container">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Cari nama/alamat..."
                            value={searchKeyword}
                            onChange={(e) => setSearchKeyword(e.target.value)}
                        />
                    </div>
                    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                        <option value="">Semua Status</option>
                        <option value="Ya">Sudah Langganan</option>
                        <option value="Tidak">Belum Langganan</option>
                        <option value="Under Prospect">Under Prospect</option>
                    </select>
                </div>
                <button className="btn-export" onClick={exportCSV}><FaDownload /> Ekspor CSV</button>
            </div>
            <div className="summary-box">
                Total: {summary.total} | Sudah Langganan: {summary.ya} | Belum Langganan: {summary.tidak} | Under Prospect: {summary.prospect}
            </div>

            {loading && <div className="loading-overlay">Memuat data peta...</div>}
            {error && <p className="error-message">{error}</p>}
            {message && <p className="success-message">{message}</p>}

            {/* Map Section */}
            <div id="map" ref={mapRef}></div>

            {/* Legend */}
            <div className="legend">
                <b>🟢 Legenda Status:</b><br />
                <div><span className="dot green-dot"></span> Sudah Langganan</div>
                <div><span className="dot red-dot"></span> Belum Langganan</div>
                <div><span className="dot orange-dot"></span> Under Prospect</div>
                <button className="btn-add-point" onClick={toggleAddForm}><FaPlus /> Tambah Titik Baru</button>
            </div>

            {/* Add Point Form */}
            {addMode && (
                <div className="add-form-overlay">
                    <div className="add-form-card">
                        <h3>Tambah Titik Baru</h3>
                        <input name="nama" value={newPointFormData.nama} onChange={handleAddFormChange} placeholder="Nama Depot" />
                        <textarea name="alamat" value={newPointFormData.alamat} onChange={handleAddFormChange} placeholder="Alamat Lengkap"></textarea>
                        <input name="telepon" value={newPointFormData.telepon} onChange={handleAddFormChange} placeholder="Telepon" />
                        <input name="jam_buka" value={newPointFormData.jam_buka} onChange={handleAddFormChange} placeholder="Jam Buka" />
                        <div className="coords-input-group">
                            <input name="latitude" value={newPointFormData.latitude} onChange={handleAddFormChange} placeholder="Latitude" readOnly={!addMode} />
                            <input name="longitude" value={newPointFormData.longitude} onChange={handleAddFormChange} placeholder="Longitude" readOnly={!addMode} />
                        </div>
                        <input name="kelurahan" value={newPointFormData.kelurahan} onChange={handleAddFormChange} placeholder="Kelurahan" />
                        <input name="kecamatan" value={newPointFormData.kecamatan} onChange={handleAddFormChange} placeholder="Kecamatan" />
                        <input name="kota_kabupaten" value={newPointFormData.kota_kabupaten} onChange={handleAddFormChange} placeholder="Kota/Kabupaten" />
                        <input name="surveyed_by" value={newPointFormData.surveyed_by} onChange={handleAddFormChange} placeholder="Surveyor" />
                        <select name="is_subscribed" value={newPointFormData.is_subscribed} onChange={handleAddFormChange}>
                            <option value="Under Prospect">Under Prospect</option>
                            <option value="Ya">Ya</option>
                            <option value="Tidak">Tidak</option>
                        </select>
                        <button className="btn-save-new" onClick={submitNewPoint}>Simpan Titik Baru</button>
                        <button className="btn-close-form" onClick={toggleAddForm}>Tutup</button>
                        <p className="form-tip">Klik di peta untuk mengisi koordinat otomatis</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WebGisPage;